/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { Observable, switchMap, map, of, BehaviorSubject, filter } from 'rxjs';
import { SequenceService } from './sequence.service';
import { SequenceRequest } from './models/request/sequence.request';
import { DateTime } from 'luxon';
import { LegView } from './models/view/leg.view';
import { SequenceView } from './models/view/sequence.view';
import { RouteParameterStore } from '../shared/services/route-parameter.store';
import { RequestState, RequestStatus } from '../shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { FlightParams } from '@shared/models/flight-params';
import { Router } from '@angular/router';
import { SequenceResponse } from './models/response/sequence.response';
import { HotelRequest } from '@shared/features/hotel/models/request/hotel.request';
import { AuthService } from '@shared/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class SequenceStore {
  currentSequence: SequenceResponse | null = null;
  selectedLeg: LegView | null = null;
  empNumber = this.authService.empNumber;

  // Observable for flight information.
  flight$ = this.getFlight();

  // Which flight URL is currently being viewed.
  private _currentFlightUrlIndex = new BehaviorSubject<number | null>(null);
  currentFlightUrlIndex$ = this._currentFlightUrlIndex.asObservable();

  // A list of URL strings representing the flights within this sequence.
  // Used for prev/next navigation.
  private _flightUrlList = new BehaviorSubject<string[]>([]);
  flightUrlList$ = this._flightUrlList.asObservable();

  constructor(
    private authService: AuthService,
    private sequenceService: SequenceService,
    private routeParameterStore: RouteParameterStore,
    private alertService: AlertService,
    private router: Router,
  ) {}

  getSequence(
    request: SequenceRequest,
  ): Observable<RequestState<SequenceView>> {
    return this.sequenceService.getSequence(request).pipe(
      map((requestState) => {
        if (requestState.hasError) {
          this.showErrorAlert(request.number);
        }

        if (!requestState.response) {
          return requestState.into<SequenceView>();
        }

        this.currentSequence = requestState.response;
        const sequenceView = new SequenceView(requestState.response);
        this.createFlightsUrlList(sequenceView);

        return requestState.into<SequenceView>(sequenceView);
      }),
    );
  }

  /** Uses route paramaters to generate sequence request and force data refresh. */
  refreshSequence(): Observable<RequestState<SequenceView>> {
    const seqRequest = this.routeParameterStore.currentSequenceParams;

    // Trigger refresh. _forceRefresh flag will get reset automatically.
    if (!seqRequest) {
      return of(
        new RequestState<SequenceView>(RequestStatus.IsCompleteNoResponse),
      );
    }

    return this.sequenceService.refreshSequence(seqRequest).pipe(
      map((requestState) => {
        if (requestState.hasError) {
          this.showErrorAlert(seqRequest.number);
        }

        if (!requestState.response) {
          return requestState.into<SequenceView>();
        }

        const sequenceView = new SequenceView(requestState.response);
        this.createFlightsUrlList(sequenceView);
        return requestState.into<SequenceView>(sequenceView);
      }),
    );
  }

  // Find sequence start and end for hotel request.
  getHotelRequest(): HotelRequest | null {
    if (
      this.currentSequence &&
      this.currentSequence.dutyPeriodSummaries.length > 0
    ) {
      const periods = this.currentSequence.dutyPeriodSummaries.length;
      const start =
        this.currentSequence.dutyPeriodSummaries[0].dutyPeriodStartTime;
      const end =
        this.currentSequence.dutyPeriodSummaries[periods - 1].dutyPeriodEndTime;

      if (start && end) {
        // Need to extend search period for last duty period hotel.
        const endPlus24 = DateTime.fromISO(end).plus({ hours: 24 }).toISO();

        return {
          // employeeNumber: this.empNumber,
          start,
          end: endPlus24,
        };
      }
    }

    return null;
  }

  /** Returns the Sabre command (hss) for the current sequence. */
  getSeqSabreCmd(): string {
    const currentSequence = this.routeParameterStore.currentSequenceParams;

    if (currentSequence) {
      const date = DateTime.fromISO(currentSequence.date, {
        zone: 'utc',
      }).toFormat('ddLLL');

      return `hss/${currentSequence.seat}/${currentSequence.number}/${date}`;
    }

    return '';
  }

  /** Flight observable is updated when route params change. Update sequence data. */
  getFlight(): Observable<RequestState<LegView>> {
    return this.routeParameterStore.flightParams$.pipe(
      filter((flightParams) => {
        return flightParams !== null;
      }),
      switchMap((flightParams) => {
        const request: SequenceRequest = {
          date: flightParams?.sequenceParams.date || '',
          seat: flightParams?.sequenceParams.seat || '',
          number: flightParams?.sequenceParams.number || '',
        };

        return this.getSequence(request).pipe(
          map((sequenceState) => {
            if (!flightParams) {
              return sequenceState.into<LegView>();
            }
            return this.mapFlightResponse(sequenceState, flightParams);
          }),
        );
      }),
      // tap((flight) => console.log(flight))
    );
  }

  refreshFlight(): Observable<RequestState<LegView>> {
    const flightParams = this.routeParameterStore.currentFlightParams;

    return this.refreshSequence().pipe(
      map((sequenceState) => {
        if (!flightParams) {
          return sequenceState.into<LegView>();
        }
        return this.mapFlightResponse(sequenceState, flightParams);
      }),
    );
  }

  nextFlight(): void {
    const currentFlightUrlIndex = this._currentFlightUrlIndex.getValue();
    const flightUrlList = this._flightUrlList.getValue();

    if (
      currentFlightUrlIndex !== null &&
      currentFlightUrlIndex < this._flightUrlList.getValue().length - 1
    ) {
      this.router.navigate([flightUrlList[currentFlightUrlIndex + 1]]);
    }
  }

  prevFlight(): void {
    const currentFlightUrlIndex = this._currentFlightUrlIndex.getValue();
    const flightUrlList = this._flightUrlList.getValue();

    if (currentFlightUrlIndex !== null && currentFlightUrlIndex > 0) {
      this.router.navigate([flightUrlList[currentFlightUrlIndex - 1]]);
    }
  }

  /** Set which URL in the list is being viewed. */
  setFlightUrlListIndex(url: string): void {
    const flightUrlList = this._flightUrlList.getValue();

    for (let i = 0; i < flightUrlList.length; i++) {
      if (url === flightUrlList[i]) {
        this._currentFlightUrlIndex.next(i);
      }
    }
  }

  private mapFlightResponse(
    sequenceState: RequestState<SequenceView>,
    flightParams: FlightParams,
  ): RequestState<LegView> {
    if (!sequenceState.response) {
      // Pass on isLoading or hasError.
      return sequenceState.into<LegView>();
    }

    // Find matching flight/leg.
    for (const dutyPeriod of sequenceState.response.dutyPeriods) {
      for (const leg of dutyPeriod.legs) {
        // Remove leading zeroes from flight numbers for accurate comparison.
        const legFlightNumber = parseInt(leg.flightNumber).toString();
        const paramsFlightNumber = parseInt(
          flightParams!.flightNumber,
        ).toString();

        if (
          legFlightNumber === paramsFlightNumber &&
          leg.depStation === flightParams!.depStation &&
          leg.arrStation === flightParams!.arrStation &&
          leg.depTimeSched?.toISODate() === flightParams!.depDate
        ) {
          const seqParams = this.routeParameterStore.currentSequenceParams;

          // Update the flight URL index (which URL in the list is being viewed).
          this.setFlightUrlListIndex(
            `/sequence/${
              sequenceState.response.sequenceNumber
            }/${seqParams?.seat}/${seqParams?.date}/flight/${
              leg.flightNumber
            }/${leg.depStation}/${
              leg.arrStation
            }/${leg.depTimeSched?.toISODate()}`,
          );

          // Return found leg.
          return sequenceState.into<LegView>(leg);
        }
      }
    }

    // Flight/leg not found.
    return new RequestState<LegView>(RequestStatus.IsCompleteNoResponse);
  }

  /** Create URLs for all flights within this sequence and return as array. */
  private createFlightsUrlList(sequence: SequenceView): void {
    const urlList: string[] = [];
    const flightParams = this.routeParameterStore.currentSequenceParams;

    if (flightParams) {
      for (const dutyPeriod of sequence.dutyPeriods) {
        for (const leg of dutyPeriod.legs) {
          urlList.push(
            `/sequence/${sequence.sequenceNumber}/${flightParams.seat}/${
              flightParams.date
            }/flight/${leg.flightNumber}/${leg.depStation}/${
              leg.arrStation
            }/${leg.depTimeSched?.toISODate()}`,
          );
        }
      }
    }

    this._flightUrlList.next(urlList);
  }

  private showErrorAlert(sequenceNumber: string): void {
    this.alertService.setAlert({
      type: 'error',
      message: `Failed to load sequence ${sequenceNumber}.`,
    });
  }
}
